import { useCallback, useEffect, useState } from "react";
import FieldLayout from "components/field/FieldLayout";
import LabAccuracy from "components/radarLab/LabAccuracy";
import LabHistory from "components/radarLab/LabHistory";
import LabPresets from "components/radarLab/LabPresets";
import LabRunPanel from "components/radarLab/LabRunPanel";
import {
  LevelPanel,
  ReadinessPanel,
  TankPanel,
  TiersBudgetsPanel,
} from "components/radarLab/LabStatusPanels";
import "components/radarLab/radarLab.css";
import { useLabRun, useLabRuns, useLabStatus, useNow } from "hooks/useRadarLab";
import { radarLabApi } from "services/radarLabApi";
import {
  finiteNumber,
  isActiveRun,
  loadSelection,
  saveSelection,
} from "utils/radarLab";

const RULER_MAX_CM = 60;

// Radar lab: stepped water-level lab runs in the greenhouse tank. The pump
// moves the level in known steps (the greenhouse backend runs each one, with a
// photo before and after) while the field node records readings, spectra and
// beat signals; every piece is labelled with the step it belongs to. The
// server enforces the limits, tiers and budgets; this page shows them.
export default function RadarLab() {
  const [selection, setSelection] = useState(loadSelection);
  const [refresh, setRefresh] = useState(0);
  const [history, setHistory] = useState(0);
  const bump = useCallback(() => setRefresh((n) => n + 1), []);
  const status = useLabStatus(selection, refresh);
  const runs = useLabRuns(history + refresh);
  const data = status.data;
  const activeRun = isActiveRun(data?.active_run) ? data.active_run : null;
  const activeId = activeRun?.id ?? null;
  const [picked, setPicked] = useState(null);
  const shownId = picked && picked !== activeId ? picked : !activeId ? runs.runs[0]?.id ?? null : null;
  const active = useLabRun(activeId, refresh);
  const shown = useLabRun(shownId, refresh);
  const now = useNow(1000);
  const [ruler, setRuler] = useState("");

  // A lab run that ends (or starts) elsewhere refreshes the history.
  useEffect(() => {
    setHistory((n) => n + 1);
  }, [activeId]);

  const choose = useCallback((key, mac) => {
    setSelection((old) => {
      const next = { ...old, [key]: mac };
      saveSelection(next);
      return next;
    });
  }, []);
  // Follow the server: a pinned choice, or the first one when none is chosen
  // (or the remembered one is gone).
  const nodeChoices = data?.choices?.nodes;
  const controllerChoices = data?.choices?.controllers;
  const serverNode = data?.node?.mac || "";
  const serverController = data?.controller?.mac || "";
  const fixedNode = !!data?.choices?.fixed?.node;
  const fixedController = !!data?.choices?.fixed?.controller;
  useEffect(() => {
    if (!data) return;
    const pick = (key, server, fixed, choices) => {
      const list = choices || [];
      const current = selection[key];
      if (fixed && server && current !== server) return choose(key, server);
      if (current && list.some((c) => c.mac === current)) return undefined;
      const next = server || list[0]?.mac || "";
      if (next && next !== current) choose(key, next);
      return undefined;
    };
    pick("node", serverNode, fixedNode, nodeChoices);
    pick("controller", serverController, fixedController, controllerChoices);
  }, [data, selection, choose, serverNode, serverController, fixedNode, fixedController, nodeChoices, controllerChoices]);

  const rulerValue = finiteNumber(ruler.trim().replace(",", "."));
  const rulerValid = rulerValue !== null && rulerValue >= 0 && rulerValue <= RULER_MAX_CM;
  const rulerError = ruler.trim() && !rulerValid ? `Enter the ruler reading in cm (0 to ${RULER_MAX_CM}).` : "";

  const start = async ({ preset, notes, rulerLevelCm, part, band }) => {
    const created = await radarLabApi.start({
      preset,
      node: data?.node?.mac,
      controller: data?.controller?.mac,
      rulerLevelCm,
      notes,
      ...(part ? { part } : {}),
      ...(band ? { band } : {}),
    });
    setRuler("");
    setPicked(null);
    bump();
    return created;
  };

  const locked = !!activeRun;
  const nodes = {
    devices: (nodeChoices || []).map((n) => ({ ...n, mac: n.mac })),
    mac: serverNode,
    select: (mac) => choose("node", mac),
    loading: status.loading && !data,
    node: data?.node ? { ...data.node, last_seen: data.node_state?.last_sample_at || null } : null,
    refresh: bump,
    error: status.error,
    locked: fixedNode || locked,
  };

  return (
    <FieldLayout
      nodes={nodes}
      section="lab"
      title="Radar lab"
      subtitle="Pump the tank in steps; each radar reading is labelled with its step."
      refresh={bump}
    >
      <div className="field-lab">
        {activeId && (
          <LabRunPanel
            run={active.data}
            error={active.error}
            now={now}
            limits={data?.limits}
            onChanged={bump}
          />
        )}
        <div className="field-lab-columns">
          <div className="field-lab-stack">
            <ReadinessPanel status={data} />
            <LabPresets
              status={data}
              ruler={rulerValue}
              rulerValid={rulerValid}
              disabled={locked}
              onStart={start}
            />
          </div>
          <div className="field-lab-stack">
            <TankPanel
              status={data}
              controllers={controllerChoices || []}
              controllerMac={serverController}
              onController={(mac) => choose("controller", mac)}
              locked={fixedController || locked}
              onPumpStopped={bump}
            />
            <LevelPanel
              level={data?.level}
              limits={data?.limits}
              ruler={ruler}
              onRuler={setRuler}
              rulerError={rulerError}
              disabled={locked}
            />
            <TiersBudgetsPanel tiers={data?.tiers} budgets={data?.budgets} />
          </div>
        </div>
        <LabAccuracy node={serverNode} controller={serverController} refresh={refresh} />
        {shownId && (
          <LabRunPanel
            run={shown.data}
            error={shown.error}
            now={now}
            limits={data?.limits}
            onChanged={bump}
          />
        )}
        <LabHistory
          runs={runs.runs}
          count={runs.count}
          error={runs.error}
          loading={runs.loading}
          selectedId={shownId ?? activeId}
          onSelect={(id) => setPicked(id === activeId ? null : id)}
        />
      </div>
    </FieldLayout>
  );
}
